import { request } from '@umijs/max';
import type {
  ApiResponse,
  ApprovalAddCcRequest,
  ApprovalCcDTO,
  ApprovalDecisionRequest,
  ApprovalNodeDTO,
  ApprovalPlannedCcDTO,
  ApprovalRecordDTO,
  ApprovalRequestDTO as SharedApprovalRequestDTO,
  ApprovalStatus,
  ApprovalTransferRequest,
  ApprovalUrgency,
  OAAttachment,
  PaginatedData,
  SubmitApprovalRequest,
} from '../../../docs/shared-types';

export type {
  ApprovalNodeDTO,
  ApprovalRecordDTO,
  ApprovalCcDTO,
  ApprovalPlannedCcDTO,
  SubmitApprovalRequest,
  ApprovalDecisionRequest,
  ApprovalTransferRequest,
  ApprovalAddCcRequest,
  OAAttachment,
  ApprovalStatus,
  ApprovalUrgency,
};

export interface ApprovalRequestDTO extends SharedApprovalRequestDTO {
  approvalNo: string;
}

export interface QueryApprovalParams {
  current?: number;
  pageSize?: number;
  keyword?: string;
  typeId?: string;
  status?: ApprovalStatus;
}

export interface ApprovalDetailResponse {
  request: ApprovalRequestDTO;
  nodes: ApprovalNodeDTO[];
  records: ApprovalRecordDTO[];
  ccs: ApprovalCcDTO[];
  plannedCcs: ApprovalPlannedCcDTO[];
}

export async function fetchTodoApprovals(
  params: QueryApprovalParams,
  options?: Record<string, any>,
) {
  const { current, ...restParams } = params;
  return request<ApiResponse<PaginatedData<ApprovalRequestDTO>>>(
    '/api/approvals/todo',
    {
      method: 'GET',
      params: { ...restParams, page: current },
      ...(options || {}),
    },
  );
}

export async function fetchMineApprovals(
  params: QueryApprovalParams,
  options?: Record<string, any>,
) {
  const { current, ...restParams } = params;
  return request<ApiResponse<PaginatedData<ApprovalRequestDTO>>>(
    '/api/approvals/mine',
    {
      method: 'GET',
      params: { ...restParams, page: current },
      ...(options || {}),
    },
  );
}

export async function fetchCcApprovals(
  params: QueryApprovalParams,
  options?: Record<string, any>,
) {
  const { current, ...restParams } = params;
  return request<ApiResponse<PaginatedData<ApprovalRequestDTO>>>(
    '/api/approvals/cc',
    {
      method: 'GET',
      params: { ...restParams, page: current },
      ...(options || {}),
    },
  );
}

export async function fetchAllApprovals(
  params: QueryApprovalParams,
  options?: Record<string, any>,
) {
  const { current, ...restParams } = params;
  return request<ApiResponse<PaginatedData<ApprovalRequestDTO>>>(
    '/api/approvals/all',
    {
      method: 'GET',
      params: { ...restParams, page: current },
      ...(options || {}),
    },
  );
}

export async function fetchApprovalDetail(id: string) {
  return request<ApiResponse<ApprovalDetailResponse>>(`/api/approvals/${id}`, {
    method: 'GET',
  });
}

export async function submitApproval(payload: SubmitApprovalRequest) {
  return request<ApiResponse<ApprovalDetailResponse>>('/api/approvals', {
    method: 'POST',
    data: payload,
  });
}

export async function resubmitApproval(
  id: string,
  payload: SubmitApprovalRequest,
) {
  return request<ApiResponse<ApprovalDetailResponse>>(
    `/api/approvals/${id}/resubmit`,
    {
      method: 'POST',
      data: payload,
    },
  );
}

export async function approveApproval(
  id: string,
  body: ApprovalDecisionRequest,
) {
  return request<ApiResponse<ApprovalRequestDTO>>(
    `/api/approvals/${id}/approve`,
    {
      method: 'POST',
      data: body,
    },
  );
}

export async function rejectApproval(
  id: string,
  body: ApprovalDecisionRequest,
) {
  return request<ApiResponse<ApprovalRequestDTO>>(
    `/api/approvals/${id}/reject`,
    {
      method: 'POST',
      data: body,
    },
  );
}

export async function withdrawApproval(id: string, reason: string) {
  return request<ApiResponse<ApprovalRequestDTO>>(
    `/api/approvals/${id}/withdraw`,
    {
      method: 'POST',
      data: { reason },
    },
  );
}

export async function transferApproval(
  id: string,
  body: ApprovalTransferRequest,
) {
  return request<ApiResponse<ApprovalRequestDTO>>(
    `/api/approvals/${id}/transfer`,
    {
      method: 'POST',
      data: body,
    },
  );
}

export async function addCcApproval(id: string, body: ApprovalAddCcRequest) {
  return request<ApiResponse<ApprovalRequestDTO>>(`/api/approvals/${id}/cc`, {
    method: 'POST',
    data: body,
  });
}
